/**
 * Пустое состояние модулей (ТЗ демо 1.4, B3 и R9): какое состояние показать, когда доступных модулей 0, и как
 * поставить название модуля во фразу, чтобы длинное занимало не больше двух строк. Чистые функции без React и
 * alias-импортов — их собирают тесты (`tests/empty-modules.test.ts`).
 */

export type EmptyModulesKind = 'loading' | 'restoreOne' | 'restoreMany' | 'locked' | 'full';

export interface EmptyModulesFacts {
  /** Удалённые модули: `'loading'` — ждём ответ; `null` — запрос не удался (дальше по порядку); иначе сколько их и
   *  пришёл ли первый (по нему «Восстановить модуль» работает на месте). */
  deleted: 'loading' | null | { total: number; first: boolean };
  emailVerified: boolean;
}

/**
 * Порядок (ТЗ демо 1.4): есть удалённые — «вернуть» (один — восстановить на месте, несколько — «Открыть
 * удалённые»); почта не подтверждена — просьба о почте; иначе — «создай свой первый модуль». Пока ответа нет —
 * скелетон: текст не должен мигнуть с «Создай модуль» на «Модуль можно вернуть». Состояния «доступ закончился»
 * нет: Demo без срока с 2026-10-22.
 */
export function emptyModulesKind({ deleted, emailVerified }: EmptyModulesFacts): EmptyModulesKind {
  if (deleted === 'loading') return 'loading';
  if (deleted && deleted.total > 0) return deleted.total === 1 && deleted.first ? 'restoreOne' : 'restoreMany';
  return emailVerified ? 'full' : 'locked';
}

/** Фраза с названием, разрезанная вокруг него. */
export interface NamedText {
  before: string;
  /** Название вместе с прилегающими к нему кавычками и знаками — переносится и обрезается отдельно. */
  name: string;
  after: string;
}

/**
 * Разрезать переведённую фразу (`«{{name}}» — в удалённых…`) вокруг метки, подставленной вместо названия.
 * Символы, прилегающие к метке без пробела (кавычки, запятая), уходят вместе с названием: иначе у обрезанного
 * названия закрывающая кавычка висела бы отдельно на следующей строке. Метки нет — вся фраза в `before`.
 */
export function splitAroundName(text: string, marker: string, name: string): NamedText {
  const at = text.indexOf(marker);
  if (at < 0) return { before: text, name: '', after: '' };
  const head = text.slice(0, at);
  const tail = text.slice(at + marker.length);
  const lead = /\S*$/.exec(head)?.[0] ?? '';
  const trail = /^\S*/.exec(tail)?.[0] ?? '';
  return {
    before: head.slice(0, head.length - lead.length),
    name: lead + name + trail,
    after: tail.slice(trail.length),
  };
}
